import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Radio, Settings as SettingsIcon, MessageSquare, ListFilter, Receipt, Tags, KeyRound, HardDriveDownload } from "lucide-react";
import { api, setLoggedOutHandler } from "@/lib/api";
import LoginPage from "@/pages/LoginPage";
import { Header } from "@/components/Header";
import { Overview } from "@/components/Overview";
import { SettingsForm } from "@/components/SettingsForm";
import { Simulator } from "@/components/Simulator";
import { Logs } from "@/components/Logs";
import { OrdersPanel } from "@/components/OrdersPanel";
import { PlansPanel } from "@/components/PlansPanel";
import { SecurityPanel } from "@/components/SecurityPanel";
import { BackupPanel } from "@/components/BackupPanel";

const TABS = [
  { id: "overview", label: "Webhook", icon: Radio },
  { id: "settings", label: "Config & IA", icon: SettingsIcon },
  { id: "simulator", label: "Simulador", icon: MessageSquare },
  { id: "logs", label: "Conversas", icon: ListFilter },
  { id: "orders", label: "Pedidos", icon: Receipt },
  { id: "sales", label: "Planos & Digital+", icon: Tags },
  { id: "security", label: "Segurança", icon: KeyRound },
  { id: "backup", label: "Backup", icon: HardDriveDownload },
];

const Dashboard = ({ user, onLogout, onPasswordChanged }) => {
  const [settings, setSettings] = useState(null);
  const load = useCallback(() => api.get("/settings").then((r) => setSettings(r.data)), []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Header settings={settings} onLogout={onLogout} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="tabs-list" data-testid="main-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger key={id} value={id} className="tab-trigger" data-testid={`tab-${id}`}>
                <Icon className="w-4 h-4" /> <span>{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="fade-in"><Overview settings={settings} onChange={setSettings} /></TabsContent>
          <TabsContent value="settings" className="fade-in">{settings && <SettingsForm settings={settings} onSaved={setSettings} />}</TabsContent>
          <TabsContent value="simulator" className="fade-in"><Simulator settings={settings} /></TabsContent>
          <TabsContent value="logs" className="fade-in"><Logs /></TabsContent>
          <TabsContent value="orders" className="fade-in"><OrdersPanel /></TabsContent>
          <TabsContent value="sales" className="fade-in"><PlansPanel /></TabsContent>
          <TabsContent value="security" className="fade-in"><SecurityPanel user={user} onChanged={onPasswordChanged} /></TabsContent>
          <TabsContent value="backup" className="fade-in"><BackupPanel /></TabsContent>
        </Tabs>
      </main>
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    setLoggedOutHandler(() => setUser(false));
    api.get("/auth/me").then((r) => setUser(r.data)).catch(async () => {
      try { setUser((await api.post("/auth/refresh")).data); } catch { setUser(false); }
    });
  }, []);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(false); };
  const changed = () => setUser((u) => ({ ...u, must_change_password: false }));

  return (
    <div className="min-h-screen app-bg text-[#F9FAFB]">
      {user === null && <p className="text-center pt-32 text-[#6B7280]" data-testid="auth-loading">Carregando...</p>}
      {user === false && <LoginPage onLogin={setUser} />}
      {user && user.must_change_password && (
        <div className="min-h-screen flex items-center justify-center px-4" data-testid="force-password-change"><SecurityPanel user={user} onChanged={changed} /></div>
      )}
      {user && !user.must_change_password && <Dashboard user={user} onLogout={logout} onPasswordChanged={changed} />}
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

export default App;
